/**
 * API endpoint constants
 * Centralized location for all API endpoints
 */

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY: '/auth/verify',
  },

  // Users
  USERS: {
    BASE: '/users',
    REGISTER: '/users/register',
    BY_ID: (userId) => `/users/${userId}`,
    BY_USER_ID: (userId) => `/users/by-user-id/${userId}`,
    PROFILE: '/users/profile',
  },

  // Operators
  OPERATORS: {
    BASE: '/operators',
    BY_ID: (operatorId) => `/operators/${operatorId}`,
    SEARCH: '/operators/search',
  },

  // Aircraft
  AIRCRAFT: {
    BASE: '/aircrafts',
    BY_ID: (aircraftId) => `/aircrafts/${aircraftId}`,
    AMENITIES: (aircraftId) => `/aircrafts/${aircraftId}/amenities`,
    PHOTOS: (aircraftId) => `/aircrafts/${aircraftId}/photos`,
  },

  // Organizations
  ORGANIZATIONS: {
    BASE: '/organizations',
    BY_ID: (orgId) => `/organizations/${orgId}`,
  },

  // Bids
  BIDS: {
    BASE: '/bids',
    BY_ID: (bidId) => `/bids/${bidId}`,
    BY_NUMBER: (bidNumber) => `/bids/by-number/${bidNumber}`,
    BY_BROKER: (brokerOrgId) => `/brokers/${brokerOrgId}/bids`,
    BY_STATUS: (status) => `/bids/by-status/${status}`,
    SHORTLIST: '/bids/shortlisted',
    OPTIONS: '/bid-options',
    OPTIONS_BY_ID: (bidOptionId) => `/bid-options/${bidOptionId}`
  },

  // Messages
  MESSAGES: {
    BASE: '/messages',
    BY_ID: (messageId) => `/messages/${messageId}`,
    UNPROCESSED: '/messages/mails/unprocessed',
    MARK_PROCESSED: (messageId) => `/messages/mails/${messageId}/process`,
    SEND: '/messages/mails/send'
  },

  // Quotes
  QUOTES: {
    BASE: '/quotes',
    BY_ID: (quoteId) => `/quotes/${quoteId}`,
    PASSENGERS: (quoteId) => `/quotes/${quoteId}/passengers`,
    PASSENGERS_BATCH: (quoteId) => `/quotes/${quoteId}/passengers/batch`,
  },

  // My Trips
  MY_TRIPS: {
    BASE: '/my-trips',
    BIDS: '/my-trips/bids',
    BY_ID: (quoteId) => `/my-trips/${quoteId}`
  },

  // Clients
  CLIENTS: {
    BASE: '/clients',
    BY_ID: (clientId) => `/clients/${clientId}`,
    PASSENGERS: (clientId) => `/clients/${clientId}/passengers`,
    PASSENGERS_BATCH: (clientId) => `/clients/${clientId}/passengers/batch`,
    CLIENT_SECRETS: '/clients/client-secrets',
    PRIORITY_VAULT: (clientId) => `/clients/${clientId}/priority-vault`,
  },
  CLIENT_PASSENGERS: {
    BY_ID: (passengerId) => `/client-passengers/${passengerId}`,
  },
  QUOTE_PASSENGERS: {
    BY_ID: (passengerId) => `/quote-passengers/${passengerId}`,
  },

  // Checkout
  CHECKOUT: {
    CHARGE: '/checkout/charge/simulated',
    CAPTURE: '/checkout/capture/simulated',
    TOKEN: '/checkout/token',
    PAYMENTINTENTS: '/checkout/payment-intents',
    SESSION: '/passport/checkout/session'
  },

  // Mercury
  MERCURY: {
    INVOICE: '/mercury/customers/invoice',
  },

  //Airports
  AIRPORTS: {
     BASE: "/airports"
  },

  // LLM
  LLM: {
    ARRIVAL_TIME: '/llm/arrival-time',
  },

  // Uploads
  UPLOADS: {
    BASE: '/upload',
  },

  // Flights
  FLIGHTS: {
    BASE: '/flights',
    BY_ID: (flightId) => `/flights/${flightId}`,
    BATCH: '/flights/batch',
  },

  // JetNet
  JETNET: {
    AIRCRAFTS: '/jetnet/aircrafts',
  },

  // FlightRadar24
  FLIGHTRADAR24: {
    LIVE_POSITIONS: '/flightradar24/live/flight-positions/light',
    FLIGHT_SUMMARY: '/flightradar24/flight-summary/light',
    AIRPORT: (code) => `/flightradar24/airports/${code}/full`,
    FLIGHT_TRACKS: '/flightradar24/flight-tracks',
    TOKEN: '/flightradar24/tracking-token',
  },

  // Stripe Connect
  STRIPE: {
    CONNECTED_ACCOUNT: (orgId) => `/organizations/${orgId}/stripe-account`,
    ONBOARDING_START: (orgId) => `/organizations/${orgId}/stripe-onboarding/start`,
    PAYMENT_LINKS: '/stripe/payment-links',
  },

  // MX Merchant
  MXMERCHANT: {
    PAYMENT_LINKS: '/mxmerchant/payment-links',
    CAPTURE_PAYMENT: '/mxmerchant/transactions/payments/capture',
  },

  // Priority Commerce (Passport)
  PASSPORT: {
    CAPTURE_PAYMENT: '/passport/checkout/payment/capture',
  },
};

/**
 * Build URL with query parameters
 * @param {string} baseUrl - Base URL
 * @param {Object} params - Query parameters
 * @returns {string} URL with query parameters
 */
export function buildUrlWithParams(baseUrl, params = {}) {
  const url = new URL(baseUrl, window.location.origin);

  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.append(key, value);
    }
  });

  return url.pathname + url.search;
}
